import React, { useState, useEffect } from 'react';
import PortfolioSummary from './components/PortfolioSummary';
import PortfolioGraph from './components/PortfolioGraph';
import PortfolioProjections from './components/PortfolioProjections';
import UserProfileService from './api';
import { buildApiUrl } from '../../services/apiConfig';
import axios from 'axios';

const UserProfileHeader = ({ customerData, useCaseId, widgetSettings = {} }) => {
  const [returnsData, setReturnsData] = useState(null);
  const [projections, setProjections] = useState(null);
  const [isLoading, setIsLoading] = useState({ returns: false, projections: false });
  const [error, setError] = useState(null);

  const headerStyle = {
    backgroundColor: widgetSettings?.headerColor || '#ffffff',
    color: widgetSettings?.textColor || '#000000'
  };

  useEffect(() => {
    if (customerData?.id) {
      fetchData(customerData.id);
    }
  }, [customerData?.id]);

  const fetchData = async (customerId) => {
    fetchReturnsData(customerId);
    fetchProjectionsData(customerId);
  };

  const fetchReturnsData = async (customerId) => {
    setIsLoading(prev => ({ ...prev, returns: true }));
    try {
      const storedUserId = localStorage.getItem('userId') || customerId;
      const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
      const formData = new FormData();
      formData.append('question', `show me the quarterly rate of returns in chronological order year by year for ${customerId} in json format`);
      formData.append('cust_id', customerId);
      formData.append('advisor_id', storedUserId);
      formData.append('use_case_id', useCaseId);
      formData.append('agent_id', "63493");

      const response = await axios.post(SQL_API, formData);

      if (response.data?.response?.result) {
        try {
          setReturnsData(JSON.parse(response.data.response.result));
        } catch (parseErr) {
          setError("Could not parse returns data");
        }
      } else if (response.data?.answer) {
        const jsonMatch = response.data.answer.match(/```json\n([\s\S]*?)\n```/);
        if (jsonMatch?.[1]) setReturnsData(JSON.parse(jsonMatch[1]));
        else setError("Could not parse returns data");
      }
    } catch (err) {
      setError("Failed to load portfolio returns data");
    } finally {
      setIsLoading(prev => ({ ...prev, returns: false }));
    }
  };

  const calculateProjections = (currentAUM) => {
    const currentYear = new Date().getFullYear();
    const annualGrowthRate = 0.07; // 7% average annual return
    
    const yearsTo2030 = 2030 - currentYear;
    const yearsTo2040 = 2040 - currentYear;
    
    // Compound growth formula: FV = PV * (1 + r)^n
    const projection2030 = Math.round(currentAUM * Math.pow(1 + annualGrowthRate, yearsTo2030));
    const projection2040 = Math.round(currentAUM * Math.pow(1 + annualGrowthRate, yearsTo2040));
    
    return { "2030": projection2030, "2040": projection2040 };
  };

  const fetchProjectionsData = async (customerId) => {
    setIsLoading(prev => ({ ...prev, projections: true }));

    try {
      // Get current portfolio value from customerData
      const currentAUM = customerData?.total_aum || customerData?.aum || 2845000;
      
      // Calculate projections locally first (reliable baseline)
      const calculatedProjections = calculateProjections(currentAUM);
      
      console.log('========== PROJECTION DATA CALCULATION ==========');
      console.log('Customer ID:', customerId);
      console.log('Current AUM:', currentAUM);
      console.log('Calculated Projections:', calculatedProjections);
      console.log('=================================================\n');
      
      // Set the calculated projections immediately
      setProjections(calculatedProjections);
      
      // Optionally try to get AI-enhanced projections in the background
      // (This won't block the UI and calculated values are already shown)
      /* Uncomment if you want AI-enhanced projections:
      const currentYear = new Date().getFullYear();
      const yearsTo2030 = 2030 - currentYear;
      const yearsTo2040 = 2040 - currentYear;
      
      const query = `Calculate portfolio value projections:
- Current: $${currentAUM.toLocaleString()}
- Years to 2030: ${yearsTo2030}
- Years to 2040: ${yearsTo2040}
Use 7% annual growth. Return JSON: {"2030": number, "2040": number}`;

      const result = await UserProfileService.sendMessage(query, [], customerId, "00001");
      
      // Only update if we get valid results
      if (result?.["2030"] && result["2030"] > currentAUM && result?.["2040"] && result["2040"] > result["2030"]) {
        setProjections(result);
        console.log('Updated with AI projections:', result);
      }
      */
      
    } catch (err) {
      console.error("========== PROJECTION ERROR ==========");
      console.error("Error in projections:", err);
      console.error("======================================\n");
      // Already have calculated projections set, no need for additional fallback
    } finally {
      setIsLoading(prev => ({ ...prev, projections: false }));
    }
  };

  return (
    <div className="rounded-lg p-4 shadow-sm border border-gray-100 mx-auto animate-[fadeInUp_0.3s_ease-out]" style={headerStyle}>
      <div className="flex flex-wrap items-stretch justify-between gap-4">
        <PortfolioSummary
          totalAum={customerData?.total_aum}
          ytdReturn={customerData?.ytd_return}
          textColor={widgetSettings?.textColor}
          isLoading={!customerData}
        />
        <PortfolioGraph
          returnsData={returnsData}
          isLoading={isLoading.returns}
          error={error}
          textColor={widgetSettings?.textColor}
        />
        <PortfolioProjections
          projections={projections}
          isLoading={isLoading.projections}
          error={error}
          textColor={widgetSettings?.textColor}
        />
      </div>
    </div>
  );
};

export default UserProfileHeader;